/**
 * Common styles and design tokens
 * Foundation for consistent styling across all components
 */

// Removed duplicate COLORS constant - using colors in commonStyles instead

// Apple system blue — THE single source for all Apple-blue values
const APPLE_BLUE = '#007AFF';
const APPLE_BLUE_HOVER = '#0066D6';
const APPLE_BLUE_PRESS = '#0055B3';
const APPLE_BLUE_TEXT_HOVER = '#0056b3';
const APPLE_BLUE_TEXT_PRESS = '#004499';

// Interaction hover tokens — reusable across all icon/button hover effects
const HOVER_DARKEN = 'hover:brightness-[0.85] transition-[filter] duration-150';

// Typography tokens
const FONT = {
  xs: "text-xs",      // 12px
  sm: "text-sm",      // 14px
  md: "text-md",      // 16px (base)
  lg: "text-lg",      // 18px
  xl: "text-xl",      // 20px
  "2xl": "text-2xl",  // 24px - for section headers
  "3xl": "text-3xl",  // 30px
};

const TRACKING = {
  tight: "tracking-tight",
  normal: "tracking-normal",
  wide: "tracking-wide",
};

const WEIGHT = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
  extrabold: "font-extrabold",
};

export const commonStyles = {
  // Layout helpers
  relative: "relative",
  relativeGroup: "relative group",
  flexCenter: "flex items-center justify-center",
  flexBetween: "flex items-center justify-between",
  
  // Typography
  typography: {
    // Large title for dashboard/page headers
    largeTitle: "text-large-title font-display font-semibold text-gray-900 mb-4",
    // Standard heading
    heading: `${WEIGHT.semibold} ${TRACKING.tight} ${FONT.lg}`,
    // Subheading
    subheading: "text-xl font-display font-medium text-gray-700 mb-2",
    // Body text
    body: "text-body font-text text-gray-600",
    // Caption/small text
    caption: "text-caption-1 font-text font-semibold text-gray-500",
    
    // Navigation text
    navText: "text-subheadline font-text font-medium",
    
    // App name/brand text base
    appNameBase: "text-title-1 font-display font-medium tracking-tight",
    
    // Section headers
    sectionHeader: "text-caption-1 font-text font-semibold uppercase tracking-wider",
    
    // Gradient text for app names/brands
    appNameGradient: "text-[#007AFF] whitespace-nowrap",
    // Premium/Pro gradient text
    premiumGradient: "bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent",

    // Apple HIG type scale — maps to SF Pro sizes in tailwind.config.ts
    apple: {
      title3: 'text-title-3 font-display font-semibold',
      headline: 'text-headline font-text font-semibold',
      body: 'text-body font-text',
      subheadline: 'text-subheadline font-text',
      footnote: 'text-footnote font-text',
      caption1: 'text-caption-1 font-text',
      caption2: 'text-caption-2 font-text',
    },
  },
  
  // Gradients
  gradients: {
    primary: "bg-gradient-to-r from-indigo-500 to-cyan-500",
    primaryBr: "bg-gradient-to-br from-indigo-500 to-cyan-500",
    primaryHover: "bg-gradient-to-r from-indigo-600 to-cyan-600",
    subtle: "bg-gradient-to-r from-indigo-500/5 to-cyan-500/5",
    success: "bg-gradient-to-r from-green-500 to-emerald-500",
    danger: "bg-gradient-to-r from-red-500 to-red-400",
    dangerHover: "bg-gradient-to-r from-red-600 to-red-600",
    emergency: "bg-gradient-to-r from-red-500 to-orange-500",
    emergencyHover: "bg-gradient-to-r from-red-600 to-orange-600",
    emergencyBr: "bg-gradient-to-br from-red-500 to-orange-500",
    purple: "bg-gradient-to-r from-purple-500 to-indigo-500",
    purpleBr: "bg-gradient-to-br from-purple-50/80 to-indigo-50/80",
    premium: "bg-gradient-to-r from-indigo-600 to-purple-600",
  },
  
  // Colors - WCAG AA compliant color system
  colors: {
    // Text colors
    text: {
      white: "text-white",
      title: "text-gray-900",  // For headings/titles
      gray: {
        // DEPRECATED: Use 'accessible' or 'accessibleBold' instead for better contrast
        lightest: "text-gray-500",     // Updated from gray-300 (improved contrast)
        lighter: "text-gray-500",      // Updated from gray-400 (improved contrast)
        light: "text-gray-500",        // WCAG AA compliant (4.5:1)
        medium: "text-gray-600",       // WCAG AA compliant (4.5:1)
        dark: "text-gray-700",         // WCAG AA compliant (7:1)
        darker: "text-gray-800",       // WCAG AAA compliant
        darkest: "text-gray-900",       // WCAG AAA compliant
        // RECOMMENDED: Use these for all text
        accessible: "text-gray-600",   // WCAG AA minimum for body text (4.5:1)
        accessibleBold: "text-gray-700", // WCAG AA for headings (7:1)
      },
      primary: "text-indigo-600",
      purple: "text-purple-600",
      purpleLight: "text-purple-700",
      blue: "text-blue-600",
      success: "text-green-600",
      successLight: "text-green-600",
      error: "text-red-600",            // Updated from red-500 (better contrast)
      warning: "text-amber-600",      // Updated from amber-500 (better contrast)
    },
    // Background colors
    background: {
      white: "bg-white",
      modal: "bg-white",  // Modal/card backgrounds
      gray: {
        lightest: "bg-gray-50",
        lightestHover: "hover:bg-gray-100",
        light: "bg-gray-100",
        medium: "bg-gray-200",
        dark: "bg-gray-700",
      },
      indigo: {
        light: "bg-indigo-500",
        medium: "bg-indigo-600",
        dark: "bg-indigo-700",
      },
      purple: {
        light: "bg-purple-500",
        medium: "bg-purple-600",
      },
      green: {
        lightest: "bg-green-50",
        light: "bg-green-100",
        medium: "bg-green-500",
      },
    },
    // Border colors
    border: {
      gray: {
        lighter: "border-gray-100",
        light: "border-gray-200",
        medium: "border-gray-300",
      },
      indigo: {
        light: "border-indigo-200",
        lightHover: "hover:border-indigo-300",
        medium: "border-indigo-300",
      },
      purple: {
        light: "border-purple-400",
        lightHover: "hover:border-purple-400",
        medium: "border-purple-500",
      },
      green: {
        light: "border-green-400",
        medium: "border-green-500",
      },
    },
    // Apple system blue — single source of truth across the app
    apple: {
      hex: APPLE_BLUE,                                        // raw value for inline styles
      text: `text-[${APPLE_BLUE}]`,                           // text color
      hoverText: `hover:text-[${APPLE_BLUE_TEXT_HOVER}]`,     // hover text
      activeText: `active:text-[${APPLE_BLUE_TEXT_PRESS}]`,    // pressed text
      bgSubtle: `bg-[${APPLE_BLUE}]/[0.06]`,                  // 6% — dropdown hover/selected
      hoverBgSubtle: `hover:bg-[${APPLE_BLUE}]/[0.06]`,       // 6% on hover
      bgLight: `bg-[${APPLE_BLUE}]/[0.10]`,                   // 10% — light blue tint bg
    },
  },
  
  // Transitions
  transitions: {
    standard: "transition duration-300",
    quick: "transition duration-200",
    slow: "transition duration-500",
    floatingLabel: "transition-all duration-500 transform origin-left cursor-text",
  },

  // Interaction hover effects
  interactions: {
    /** Apple-style icon hover — dims to 85% brightness */
    hoverDarken: HOVER_DARKEN,
    /** Opacity reveal — icon goes from muted to full on hover */
    hoverReveal: 'opacity-85 hover:opacity-100 transition-opacity duration-150',
    /** Apple muted gray for contact action icons (copy, call, sms, email) */
    contactActionIcon: 'text-[#8E8E93] hover:text-[#636366] transition-colors duration-150',
  },
  
  // Shadows
  shadows: {
    none: "shadow-none",
    sm: "shadow-sm",
    md: "shadow-md",
    lg: "shadow-lg",
    xl: "shadow-xl",
    "2xl": "shadow-2xl",
    inner: "shadow-inner",
    // Card shadow combinations with hover effects
    card: "shadow-md hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-shadow duration-300",
    cardItem: "shadow-sm hover:shadow-md transition-shadow duration-200",
  },
  
  // Common layout patterns
  layouts: {
    // Flex patterns
    flexCenter: "flex items-center justify-center",
    flexStart: "flex items-start",
    flexItemsCenter: "flex items-center",
    flexCol: "flex flex-col",
    flexColCenter: "flex flex-col items-center justify-center",
    flexBetween: "flex items-center justify-between",

    // Spacing patterns
    spaceX3: "space-x-3",
    spaceY2: "space-y-2",
    spaceY3: "space-y-3",
    spaceY4: "space-y-4",
    spaceY5: "space-y-5",
    gap1: "gap-1",
    gap2: "gap-2",
    gap3: "gap-3",
    gap4: "gap-4",

    // Card footer icon spacing
    cardFooterIconGap: "gap-4", // Standard gap between footer action icons (Home, ID Card, Customer Care group)
    cardFooterGroupGap: "gap-1", // Smaller gap for grouped icons (e.g., Customer Care + Local Agent, Calendar + Bell)
    cardFooterSectionGap: "gap-4", // Gap between different sections (left side and Calendar/Notes section)

    // Page container - standard content wrapper for all pages
    pageContainer: "max-w-7xl mx-auto px-8 sm:px-12",

    // Page header - consistent header layout for all entity pages (Address, Vehicles, etc.)
    pageHeader: "mb-6 w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4",

    // Page header icon container - gradient icon box
    pageHeaderIcon: "flex items-center justify-center w-12 h-12 rounded-2xl flex-shrink-0 bg-gradient-to-br from-indigo-500 to-cyan-500 shadow-lg shadow-indigo-500/20",

    // Page header title section
    pageHeaderTitle: "flex items-center gap-3",

    // Page header actions container - buttons aligned to the right
    pageHeaderActions: "sm:ml-auto flex-shrink-0 flex items-center gap-3",
  },
  
  // Spacing tokens
  spacing: {
    xs: "p-1",    // 4px
    sm: "p-2",    // 8px
    md: "p-4",    // 16px
    mdLg: "p-5",  // 20px
    lg: "p-6",    // 24px
    xl: "p-8",    // 32px
  },
  
  // Horizontal padding tokens
  paddingX: {
    sm: "px-2",   // 8px
    md: "px-4",   // 16px
    lg: "px-6",   // 24px
    xl: "px-8",   // 32px
  },

  // Button-specific padding tokens
  buttonPaddingX: {
    sm: "px-1",   // 4px
    md: "px-2",   // 8px
    lg: "px-4",   // 16px
    xl: "px-6",   // 24px
  },
  
  // Margin tokens
  margin: {
    left3: "ml-3",
    right3: "mr-3",
    top1: "mt-1",
    top4: "mt-4",
    top5: "mt-5",
    bottom2: "mb-2",
    bottom4: "mb-4",
    bottom6: "mb-6",
  },
  
  // Border radius patterns
  radius: {
    none: "rounded-none",
    sm: "rounded-sm", 
    md: "rounded-md",
    lg: "rounded-lg",
    xl: "rounded-xl",
    "2xl": "rounded-2xl",
    "3xl": "rounded-3xl",
    full: "rounded-full",
  },
  
  // Common component patterns
  components: {
    // Logo container with gradient background
    logoContainer: `inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-cyan-500 shadow-[0_2px_6px_rgba(0,0,0,0.08)]`,

    // Avatar container with round shape (same as logo but circular)
    avatarContainer: `inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 shadow-lg shadow-cyan-500/30`,

    // Navigation button base
    navButton: "w-full flex items-center pl-6 pr-3 py-3 group text-left",

    // Dropdown menu item base - Apple rhythm: 40-44px height
    menuItem: "group w-full min-h-[40px] px-3 py-2.5 text-left rounded-2xl transition-colors duration-[120ms] ease-in-out flex items-center gap-3",

    // Menu item hover variants - subtle hover effects
    menuItemHover: {
      default: "hover:bg-black/[0.04]",
      primary: "hover:bg-indigo-50",
      danger: "hover:bg-red-500/[0.05]",
    },

    // Menu section header - Apple style: smaller, lighter, less padding
    menuSectionHeader: "text-xs font-medium text-gray-500 px-3 pt-2 pb-1 uppercase tracking-[0.08em]",

    // Interactive button - WCAG AA compliant touch target
    interactiveButton: "min-w-[44px] min-h-[44px] inline-flex items-center justify-center transition-all duration-200",

    // Apple icon-only button — muted gray, darkens on hover (for toolbars & card menus)
    iconButton: "p-2 text-[#8E8E93] hover:text-[#636366] transition-colors duration-150",
    // Same as iconButton but with disabled state support
    iconButtonDisabled: "p-2 text-[#8E8E93] hover:text-[#636366] transition-colors duration-150 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-[#8E8E93]",

    // Card footer icon button — 24×24 hit target with darken-on-hover
    cardFooterIcon: `flex items-center justify-center w-6 h-6 cursor-pointer ${HOVER_DARKEN}`,

    // Apple section icon — muted gray for decorative (non-interactive) icons in section headers
    sectionIcon: 'text-[#8E8E93]',

    // Apple grouped background — iOS-style inset surface
    groupedBg: 'bg-[#F2F2F7]',

    // Apple muted chip — pill badge for type labels, categories, tags (system blue)
    chip: 'px-2.5 py-0.5 rounded-full bg-[rgba(0,122,255,0.1)] text-[#007AFF] text-[12px] font-semibold',

    // Apple inset card — white card with subtle border for content groups
    insetCard: 'bg-white rounded-2xl p-4 border border-gray-200',

    // Apple field row — subtle inset row for displaying a value
    fieldRow: 'bg-gray-50 rounded-2xl px-4 py-3',

    // Section header styling
    sectionHeader: "text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3",

    // Card styling for Quick Access differentiation
    quickAccessCard: "bg-gradient-to-br from-indigo-50/50 to-cyan-50/50 rounded-3xl",

    // Border separator
    borderSeparator: "border-b border-gray-200",

    // Section separator for modal/card sections
    sectionSeparator: "border-t border-gray-100",

    // Gradient divider for card sections
    gradientDivider: "absolute top-0 left-0 right-0 h-px opacity-20 mt-4 bg-gradient-to-r from-transparent via-indigo-400 via-cyan-400 to-transparent",
    
    // Glass morphism glow effect
    glassGlowEffect: "absolute inset-0 rounded-2xl bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500",
    
    // Glass morphism button - frosted glass effect for hamburger and navigation
    glassMorphismButton: "relative p-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/30 shadow-lg shadow-gray-400/20 hover:bg-white/20 hover:backdrop-blur-lg hover:border-white/40 hover:shadow-2xl hover:shadow-gray-500/30 hover:scale-105 transition-all duration-500 ease-out group",

    // Modal container - centered modal card
    modalContainer: "bg-white rounded-3xl shadow-2xl w-full pointer-events-auto transform transition-all relative",

    // Modal overlay wrapper - fixed positioning for modal centering
    modalOverlayWrapper: "fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none",
    
    // Hamburger lines container
    hamburgerContainer: "flex flex-col items-center space-y-1.5",
    
    // Hamburger line styles
    hamburgerLine: {
      // Base line style
      base: "h-0.5 bg-gray-600/80 rounded-full backdrop-blur-sm transform transition-all duration-500 group-hover:bg-gray-700 group-hover:scale-110 group-hover:shadow-sm",
      // Line widths for cascading effect
      wide: "w-6", // Top line
      medium: "w-5", // Middle line
      narrow: "w-4", // Bottom line
    },

    // Sidebar design tokens — Apple-inspired, reusable across sidebar components
    sidebar: {
      // Surface
      background: "bg-[#F7F7F8]",
      borderRight: "1px solid rgba(0, 0, 0, 0.06)",
      // Menu/hamburger button — no shadow, bg on hover only
      menuButton: "p-2.5 rounded-[10px] hover:bg-black/[0.05] transition-all duration-200",
      // Nav icon container — macOS System Settings: neutral gray bg for active
      navIcon: {
        base: "p-2.5 rounded-xl transition-all duration-200",
        active: "bg-black/[0.06]",
        inactive: "group-hover:bg-black/[0.04]",
      },
      // Nav icon color — dark for active (macOS System Settings style)
      navIconColor: {
        active: "text-[#1C1C1E]",
        inactive: "text-gray-500 group-hover:text-gray-700",
      },
      // Nav label typography — 15px, tight line-height, weight-driven hierarchy
      navText: {
        base: "whitespace-nowrap text-[15px] leading-[1.25] transition-colors duration-200",
        active: "font-semibold text-[#1C1C1E]",
        inactive: "font-medium text-[#4B5563] group-hover:text-gray-700",
      },
      // Active indicator (left bar) — hidden for clean macOS look
      activeIndicator: "absolute left-0 top-1/2 -translate-y-1/2 h-7 w-[2px] rounded-full hidden",
      activeIndicatorGradient: "#1C1C1E",
      // Consistent icon stroke width
      iconStrokeWidth: 1.75 as number,
      // Storage widget (sidebar footer) — Apple progressive disclosure
      storageText: {
        title: "text-sm font-medium text-gray-900",
        meta: "text-xs text-gray-500 tabular-nums",
        metaReveal: "mt-2 text-xs text-gray-500 opacity-0 transition-opacity group-hover:opacity-100",
      },
      storageProgress: {
        track: "mt-3 w-full h-[2px] rounded-full bg-black/10 overflow-hidden",
        bar: "h-full rounded-full transition-all duration-300",
      },
    },

    // Autocomplete / suggestion panel tokens — shared by SearchableAutocomplete & GoogleAddressAutocomplete
    autocompletePanel: {
      container: 'mt-2 rounded-2xl bg-white border border-[#E5E5EA] overflow-hidden',
      containerBase: 'rounded-2xl bg-white border border-[#E5E5EA] overflow-hidden',
      /** Gap between trigger and panel — downward (includes top-full positioning) */
      gapDown: 'top-full mt-2',
      /** Gap between trigger and panel — upward (includes bottom-full positioning) */
      gapUp: 'bottom-full mb-2',
      list: 'max-h-60 overflow-y-auto',
      item: 'flex items-center gap-3 px-4 cursor-pointer transition-colors duration-150',
      itemHover: 'hover:bg-[#F2F2F7]',
      icon: 'w-4 h-4 flex-shrink-0',
      header: 'py-2.5 px-4',
      footer: 'px-4 py-2 bg-[#F2F2F7] border-t border-[#E5E5EA]',
      footerText: 'text-xs text-gray-500',
    },

    // Apple-style action button tokens — single source of truth
    appleButton: {
      colors: {
        secondary: { bg: '#F2F2F7', hover: '#E5E5EA', press: '#DCDCE0', disabledBg: '#F2F2F7', text: 'text-[#1C1C1E]' },
        primary:   { bg: APPLE_BLUE, hover: APPLE_BLUE_HOVER, press: APPLE_BLUE_PRESS, disabledBg: '#C7C7CC', text: 'text-white' },
        danger:    { bg: '#FF453A', hover: '#E03A2F', press: '#C8332A', disabledBg: '#C7C7CC', text: 'text-white' },
      },
      sizes: {
        compact: 'h-[38px] rounded-[10px] text-[15px]',
        full:    'h-[52px] rounded-[14px] text-[16px]',
      },
      base: 'flex items-center justify-center font-semibold tracking-[-0.01em] border-none disabled:cursor-not-allowed',
      transition: 'background-color 120ms ease-out, transform 80ms ease-out',
    },

    // Apple-style text link tokens
    textLink: {
      base: 'inline-flex items-center gap-1.5 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
      color: `text-[${APPLE_BLUE}]`,
      hover: 'hover:underline',
    },
  },

  // Legacy button layouts (keeping for backwards compatibility)
  buttonLayouts: {
    flex: "flex gap-3",
    flexWithPadding: "flex gap-3 pt-2",
  },
  
  // Typography scales
  font: FONT,
  weight: WEIGHT,
  tracking: TRACKING,
  
  // Common button base (for composition)
  button: {
    base: `inline-flex items-center justify-center font-medium rounded-lg transition duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2`,
    sizes: {
      sm: "min-h-[36px] px-3 text-sm",            // 36px minimum
      md: "min-h-[44px] px-4 text-sm",            // 44px WCAG AA compliant
      lg: "min-h-[48px] px-6 text-base",          // 48px comfortable
    }
  },

  // Focus states for accessibility
  focusStates: {
    ring: "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
    ringWhite: "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-500",
    /** Standard input focus — blue-400 border, no ring */
    input: "focus:outline-none focus:ring-0 focus:border-blue-400",
    /** Input focus for error state — red border, no ring */
    inputError: "focus:outline-none focus:ring-0 focus:border-red-500",
  },
  
  // Icon sizes
  iconSizes: {
    xs: "w-3 h-3",      // 12px
    sm: "w-4 h-4",      // 16px
    md: "w-5 h-5",      // 20px
    lg: "w-6 h-6",      // 24px
    xl: "w-8 h-8",      // 32px
    "2xl": "w-10 h-10", // 40px
    "3xl": "w-12 h-12", // 48px
    "4xl": "w-16 h-16", // 64px
  },

  // Avatar/Profile picture sizes
  avatarSizes: {
    sm: "w-10 h-10",    // 40px - small avatar
    md: "w-16 h-16",    // 64px - medium avatar
    lg: "w-24 h-24",    // 96px - large avatar
    xl: "w-32 h-32",    // 128px - extra large (profile edit)
  },

  // Step indicator styles for wizards
  stepIndicator: {
    circle: "w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300",
    active: "bg-indigo-600 text-white shadow-lg shadow-indigo-500/50",
    completed: "bg-green-500 text-white",
    inactive: "bg-gray-200 text-gray-500",
    connector: "w-10 h-1 mx-1 transition-all duration-300",
    connectorActive: "bg-green-500",
    connectorInactive: "bg-gray-200",
  },

  // Toggle switch styles
  toggle: {
    track: "relative w-9 h-5 rounded-full transition-colors duration-300 bg-indigo-500",
    knob: "absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-300",
    knobActive: "translate-x-4",
    knobInactive: "translate-x-0",
  },

  // Radio button styles
  radio: {
    base: "w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0",
    selected: "border-purple-500 bg-purple-500",
    unselected: "border-gray-300",
    dot: "w-1.5 h-1.5 rounded-full bg-white",
  },

  // Profile picture edit button
  profilePictureButton: "absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-indigo-600 hover:bg-indigo-700 rounded-full flex items-center justify-center shadow-lg transition-all transform hover:scale-110 border-2 border-white",
  
  // Logo specific sizes
  logoSizes: {
    collapsed: "w-14 h-14", // 56px for collapsed sidebar
    expanded: "w-14 h-14",  // 56px for expanded sidebar
    small: "w-12 h-12",     // 48px for small contexts
    large: "w-16 h-16",     // 64px for large contexts
  },
  
  // Animation classes
  animations: {
    fadeIn: "animate-fade-in",
    spin: "animate-spin",
    pulse: "animate-pulse",
    bounce: "animate-bounce",
  },

  // Utility classes
  utilities: {
    flexShrink0: "flex-shrink-0",
    flex1: "flex-1",
    noWrap: "whitespace-nowrap",
    textLeft: "text-left",
    textCenter: "text-center",
    textRight: "text-right",
    uppercase: "uppercase",
    trackingWider: "tracking-wider",
    leadingRelaxed: "leading-relaxed",
    cursorPointer: "cursor-pointer",
    wFull: "w-full",
    selfCenter: "self-center",
    disabledOpacity: "disabled:opacity-50 disabled:cursor-not-allowed",
  },

  // Height tokens - standardized max heights for modals and dropdowns
  maxHeight: {
    modal: "!max-h-[680px]",  // Maximum modal height - consistent across all modals
    dropdown: "max-h-[60vh]",     // Maximum dropdown height (60% viewport)
    list: "max-h-[400px]",        // Standard list height
    picker: "max-h-[320px]",      // Time/date picker height
  },

  // Width tokens - standardized widths for modals and containers
  width: {
    sidebar: {
      collapsed: "w-[18%]",       // Collapsed sidebar width (18%)
      expanded: "w-[82%]",        // Expanded content width (82%)
    },
    modal: {
      sm: "w-full max-w-md",      // Small modal (448px)
      md: "w-full max-w-lg",      // Medium modal (512px)
      lg: "w-full max-w-2xl",     // Large modal (672px)
      xl: "w-full max-w-4xl",     // Extra large modal (896px)
    },
    dropdown: "w-48",             // Standard dropdown width
  },

  // Position tokens - common positioning values
  position: {
    offset: {
      sm: "-bottom-2",            // Small offset (8px)
      md: "-bottom-4",            // Medium offset (16px)
      lg: "-bottom-6",            // Large offset (24px)
    },
  },

  // Scrollbar tokens
  scrollbar: {
    thin: 'apple-scrollbar',  // 6px translucent thumb, no track (defined in globals.css)
  },

  // Z-index tokens - layering system
  zIndex: {
    dropdown: "z-10",
    sticky: "z-20",
    overlay: "z-40",
    modal: "z-50",
    tooltip: "z-[60]",
    toast: "z-[70]",
  },
};